import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import NumberInput from 'components/inputs/number';
import Text from 'components/inputs/text';
import ToggleSwitch from 'components/inputs/toggle-switch';
import { AttributeType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useState } from 'react';
import axios from 'utils/api';
import { removeNullishValues } from 'utils/helpers';
import { errorModal, successModal } from 'utils/ui';

export default function AttributesPage() {
  const dispatch = useAppDispatch();
  const { attributes } = useAppSelector((state) => state.admin);
  const [newAttributes, setNewAttributes] = useState<AttributeType[]>([]);

  useEffect(() => {
    setNewAttributes(attributes);
  }, [attributes]);

  const handleValueChange = (attributeId: number, key: string, value: any) => {
    setNewAttributes((oldValues) => {
      const newValues = oldValues.map((att) => {
        if (att.id === attributeId) {
          return {
            ...att,
            [key]: value
          };
        }
        return att;
      });
      return newValues;
    });
  };

  const handleSave = async () => {
    try {
      await axios.patch(
        'attributes',
        newAttributes.map((att) => removeNullishValues(att))
      );
      dispatch(fetchDefaultAdminData());
      successModal(`Attributes updated.`);
    } catch {
      errorModal('Something went wrong.');
    }
  };

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>Attributes</div>
      <div className='max-h-[50vh] overflow-auto'>
        <table className='min-w-full border-separate border-spacing-0 divide-y divide-gray-300'>
          <thead>
            <tr>
              <th
                scope='col'
                className='sticky top-0 z-10 border-b border-gray-300 bg-gray-100 pb-3.5 pl-3 pr-0 text-left font-semibold text-gray-900'
              >
                Label
              </th>
              {[
                'Type',
                'Placeholder',
                'Priority',
                'Group',
                'Min',
                'Max',
                'Prefix',
                'Suffix',
                'Tooltip',
                'Inheritable',
                'Advanced'
              ].map((header) => (
                <th
                  key={header}
                  scope='col'
                  className='sticky top-0 z-10 border-b border-gray-300 bg-gray-100 px-3 pb-3.5 text-left font-semibold text-gray-900'
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {newAttributes
              .toSorted((a, b) => a.label.localeCompare(b.label))
              .map((attribute) => (
                <tr key={attribute.id}>
                  <td className='whitespace-nowrap border-b border-gray-200 py-4 pl-3 pr-0 text-gray-900'>
                    <div className='w-[200px]'>
                      <Text
                        value={attribute.label}
                        onChange={(value) =>
                          handleValueChange(attribute.id, 'label', value)
                        }
                        placeholder='Label...'
                      />
                    </div>
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    {attribute.type}
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    {attribute.type !== 'checkbox' && (
                      <div className='w-[150px]'>
                        <Text
                          value={attribute.placeholder}
                          onChange={(value) =>
                            handleValueChange(
                              attribute.id,
                              'placeholder',
                              value
                            )
                          }
                          placeholder='Placeholder...'
                        />
                      </div>
                    )}
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    <div className='w-[70px]'>
                      <NumberInput
                        value={attribute.priority}
                        onChange={(value) =>
                          handleValueChange(attribute.id, 'priority', value)
                        }
                        placeholder='Priority...'
                      />
                    </div>
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    <div className='w-[150px]'>
                      <Text
                        value={attribute.group}
                        onChange={(value) =>
                          handleValueChange(attribute.id, 'group', value)
                        }
                        placeholder='Group...'
                      />
                    </div>
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    {attribute.type === 'number' ? (
                      <div className='w-[70px]'>
                        <NumberInput
                          value={attribute.min}
                          onChange={(value) =>
                            handleValueChange(attribute.id, 'min', value)
                          }
                          placeholder='Min...'
                        />
                      </div>
                    ) : null}
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    {attribute.type === 'number' ? (
                      <div className='w-[70px]'>
                        <NumberInput
                          value={attribute.max}
                          onChange={(value) =>
                            handleValueChange(attribute.id, 'max', value)
                          }
                          placeholder='Max...'
                        />
                      </div>
                    ) : null}
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    {attribute.type === 'number' ||
                    attribute.type === 'text' ? (
                      <div className='w-[100px]'>
                        <Text
                          value={attribute.prefix}
                          onChange={(value) =>
                            handleValueChange(attribute.id, 'prefix', value)
                          }
                          placeholder='Prefix...'
                        />
                      </div>
                    ) : null}
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    {attribute.type === 'number' ||
                    attribute.type === 'text' ? (
                      <div className='w-[100px]'>
                        <Text
                          value={attribute.suffix}
                          onChange={(value) =>
                            handleValueChange(attribute.id, 'suffix', value)
                          }
                          placeholder='Suffix...'
                        />
                      </div>
                    ) : null}
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    <div className='w-[200px]'>
                      <Text
                        value={attribute.tooltip}
                        onChange={(value) =>
                          handleValueChange(attribute.id, 'tooltip', value)
                        }
                        placeholder='Tooltip...'
                      />
                    </div>
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    <ToggleSwitch
                      value={attribute.inheritable}
                      onChange={(value) =>
                        handleValueChange(attribute.id, 'inheritable', value)
                      }
                    />
                  </td>
                  <td className='whitespace-nowrap border-b border-gray-200 px-3 py-4'>
                    <ToggleSwitch
                      value={attribute.advanced}
                      onChange={(value) =>
                        handleValueChange(attribute.id, 'advanced', value)
                      }
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div>
        <Button text='Save' color='green' onClick={handleSave} />
      </div>
    </div>
  );
}
